<template>
  <overlay
    key="lookbook-product-list-modal"
    block-class="lookbook-product-list-modal"
    namespace="lookbook-product-list-modal"
    type="shelf-modal"
    :show-close="false"
    mobile-drag-close
    direction="up"
  >
    <template #body>
      <teleport to="body">
        <transition name="fade">
          <div
            v-if="lastAddedProductId"
            class="lookbook-product-list-modal__success"
          >
            <div class="lookbook-product-list-modal__success-left">
              <icon-check />

              <p
                class="
                  lookbook-product-list-modal__success-title
                  text-body-2xs-regular
                "
                v-text="successMessage"
              />
            </div>

            <btn
              class="lookbook-product-list-modal__view-cart-button"
              :label="$string('product.cta.view_cart')"
              type="button"
              :url="$variable('routes.cart.url')"
              modifiers="text center"
            />
          </div>
        </transition>
      </teleport>

      <div
        v-if="!showBackInStock"
        class="lookbook-product-list-modal__header-wrapper"
      >
        <button
          v-if="viewedProduct?.id"
          class="lookbook-product-list-modal__close-atc"
          type="button"
          @click="closeAtc"
        >
          <icon-arrow-left />

          <span
            class="
              lookbook-product-list-modal__close-atc-label
              text-body-2xs-regular
            "
            v-text="$string('lookbook.return_to_look')"
          />
        </button>

        <h2
          v-else
          class="
            lookbook-product-list-modal__header
            text-body-2xs-regular
          "
          :class="{
            'font-family-freight large-font': location?.key === 'plpHotspot'
          }"
          v-text="$string('lookbook.shop_the_look')"
        />
      </div>

      <template v-if="viewedProduct?.id">
        <div
          v-if="!showBackInStock"
          class="lookbook-product-list-modal__details"
        >
          <div
            :key="viewedProduct?.id"
            class="lookbook-product-list-modal__images"
          >
            <product-card
              card-layout="small"
              enable-image-carousel
              :enable-flat-image="false"
              enable-variant-image
              :image-background-color="getImageBackgroundColor()"
              :image-is-linked="false"
              :load-secondary-image="false"
              padded-images
              :product="viewedProduct || false"
              :show-badges="false"
              :show-product-content="false"
              :show-quick-add="false"
              :show-swatches="false"
              :product-card-location="location || {
                key: 'sectionHotspot',
              }"
              :show-wishlist="false"
            />
          </div>

          <div class="lookbook-product-list-modal__product-info">
            <div class="lookbook-product-list-modal__product">
              <p
                :id="`product-title-${viewedProduct?.handle}`"
                class="
                  lookbook-product-list-modal__title
                  text-body-2xs-regular
                "
                :class="{
                  'font-family-freight large-font': location?.key === 'plpHotspot'
                }"
                v-text="viewedProduct?.title"
              />

              <product-prices
                class="lookbook-product-list-modal__prices"
                :available="viewedProduct?.available"
                :compare-at-price="viewedProduct?.compareAtPrice?.amount"
                :price="viewedProduct?.price?.amount"
                :varies="viewedProduct?.price?.varies"
                class-price="text-body-2xs-regular"
                class-compare-at-price="text-body-2xs-regular"
              />

              <p
                class="text-body-2xs-regular"
                v-text="viewedProductSwatch"
              />
            </div>

            <div class="lookbook-product-list-modal__bookmark_icon">
              <wishlist-button
                v-show="renderWishlistBtn"
                class="lookbook-product-list-modal__wishlist"
                :product="viewedProduct"
                template="hotspot"
                :variant="viewedProduct?.selectedOrFirstAvailableVariant"
              />
            </div>
          </div>
        </div>

        <quick-add-to-cart
          is-mobile-inline
          is-stl-type
          :number-of-sizes-per-row="6"
          placement="lookbook"
          :product="viewedProduct"
          show-confirmation-button
          show-view-product-button
          size-guide-position="bottom"
          :product-card-location="location || {
            key: 'sectionHotspot',
          }"
        />
      </template>

      <div
        v-else
        class="lookbook-product-list-modal__carousel-wrapper"
      >
        <splide
          class="lookbook-product-list-modal__mobile-carousel col xs12"
          :has-track="false"
          :options="getSplideConfigs()"
        >
          <splide-track>
            <splide-slide
              v-for="(product, productIndex) of products"
              :key="product.id"
            >
              <product-card
                card-layout="regular"
                custom-atc
                :enable-flat-image="false"
                :enable-image-carousel="false"
                enable-variant-image
                :enable-secondary-image="false"
                :image-is-linked="false"
                :product="product"
                :product-card-location="location || {
                  key: 'sectionHotspot',
                }"
                :image-background-color="getImageBackgroundColor()"
                :show-badges="false"
                show-product-content
                show-quick-add
                :show-swatches="false"
                :show-wishlist="false"
                @custom-quick-add="handleAddToCart(product, productIndex)"
              />
            </splide-slide>
          </splide-track>
        </splide>
      </div>
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Product List (product-list)
 * -----------------------------------------------------------------------------
 * Lookbook Mobile Product list modal.
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapState } from 'vuex'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import ProductCard from '~global/product-card/product-card'
import ProductPrices from '~global/product-prices/product-prices'
import QuickAddToCart from '~async/quick-add-to-cart/quick-add-to-cart'
import WishlistButton from '~async/wishlist-button/wishlist-button'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconCheck from '~icons/general/check.svg'

export default {
  name: 'LookbookProductListModal',

  components: {
    Btn,
    IconArrowLeft,
    IconCheck,
    Overlay,
    ProductCard,
    ProductPrices,
    QuickAddToCart,
    Splide,
    SplideSlide,
    SplideTrack,
    WishlistButton,
  },

  data() {
    return {
      active: false,
      products: [],
      splide: {},
      viewedProduct: {},
      viewedProductIndex: 0,
      showBackInStock: false,
      showSuccessMessage: false,
      blockConfigs: [],
      index: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      lastAddedProductId: (state) => state.quickAddToCart.lastAddedProductId,
      shopTheLookSelectedProducts: (state) => state.shopTheLook.selectedProducts,
      shopTheLookBlockConfigs: (state) => state.shopTheLook.blockConfigs,
      shopTheLookIndex: (state) => state.shopTheLook.index,
      stateSize: (state) => state.quickAddToCart.size,
      activeOverlay: (state) => state.overlays.active[0],
      location: (state) => state.shopTheLook.sectionLocation,
      stateProduct: (state) => state.quickAddToCart.product,
    }),

    /**
     * Compute success message.
     * @returns {String}
     */
    successMessage() {
      if (['os', 'one size'].includes(this.stateSize?.toLowerCase())) {
        return this.$string('product.cta.added_to_cart')
      }

      return this.$string('product.cta.success_added_size', {
        replace: { size: this.stateSize },
      })
    },

    /**
     * Compute viewed product swatch.
     * @returns {String}
     */
    viewedProductSwatch() {
      if (!this.viewedProduct || !this.$variable('settings.product.productSwatchOptionNames')) {
        return ''
      }

      const variant = this.viewedProduct?.selectedOrFirstAvailableVariant
      const swatchOption = variant?.selectedOptions?.find((option) => {
        return this.$variable('settings.product.productSwatchOptionNames')?.includes(option.name?.toLowerCase())
      })

      if (!swatchOption) {
        return ''
      }

      return this.$string(`product.cta.${swatchOption.name.toLowerCase()}_label`, {
        replace: {
          [swatchOption.name.toLowerCase()]: swatchOption.value || '',
        },
      })
    },

    /**
     * Renders wishlist button in all cases
     */
    renderWishlistBtn() {
      return true
    },
  },

  watch: {
    lastAddedProductId() {
      if (this.lastAddedProductId) {
        this.closeAtc()
      }
    },
    shopTheLookIndex: {
      handler() {
        this.index = this.shopTheLookIndex
      },
      immediate: true,
    },
    shopTheLookSelectedProducts() {
      this.products = this.shopTheLookSelectedProducts
    },

    shopTheLookBlockConfigs() {
      this.blockConfigs = this.shopTheLookBlockConfigs
    },

    activeOverlay(activeOverlay) {
      if (activeOverlay?.namespace === 'lookbook-product-list-modal' && this.active) {
        this.products = this.shopTheLookSelectedProducts
        this.viewedProduct = {}
      } else if (activeOverlay?.namespace === 'lookbook-product-list-modal' && this.active === false) {
        this.active = true
      } else if (activeOverlay?.namespace === 'sizeGuideDrawer' && this.active) {
        this.products = this.shopTheLookSelectedProducts
        this.viewedProduct = this.stateProduct
      } else if (activeOverlay?.namespace !== 'lookbook-product-list-modal' && this.active) {
        this.active = false
        this.resetModal()
      }
    },
  },

  methods: {

    getImageBackgroundColor() {
      return this.blockConfigs[this.index]?.cardBackground || '--color-brand-light-gray'
    },

    /**
     * Get splide configs
     */
    getSplideConfigs() {
      return {
        arrows: false,
        autoplay: false,
        autoWidth: false,
        direction: 'ltr',
        drag: true,
        fixedWidth: this.viewedProduct?.id ? '100%' : '45%',
        pagination: false,
        pauseOnHover: false,
        perMove: 1,
        perPage: 1,
        speed: this.$timing('normal'),
        start: 0,
        type: 'slide',
        width: this.viewedProduct?.id ? '100%' : '',
      }
    },

    /**
     * Reset modal data on close
     */
    resetModal() {
      this.viewedProduct = {}
      this.viewedProductIndex = 0
    },

    /**
     * Close ATC and show products only
     */
    closeAtc() {
      this.viewedProduct = {}
    },

    /**
     * Show single product and atc size grid.
     * @param {Object} product - Formatted product object.
     * @param {Number} index - Product forloop index.
     */
    handleAddToCart(product, index) {
      this.viewedProduct = product
      this.viewedProductIndex = index
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },
  },

}

</script>

<style lang="scss">
@import './lookbook-product-list-modal.scss';
</style>
